import { useState, useEffect } from 'react';
import { Users as UsersIcon, Plus, X, ShieldCheck, Warning, MapPin, Check } from '@phosphor-icons/react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useLocationContext } from '../contexts/LocationContext';

const ROLES = [
  { value: 'admin', label: 'System Admin' },
  { value: 'executive', label: 'Executive' },
  { value: 'factory_manager', label: 'Factory Manager' },
  { value: 'quality_manager', label: 'Quality Manager' },
  { value: 'warehouse_manager', label: 'Warehouse Manager' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'dispatch_user', label: 'Dispatch User' },
  { value: 'procurement_user', label: 'Procurement User' },
  { value: 'finance_user', label: 'Finance User' },
  { value: 'viewer', label: 'Viewer' },
];

const ROLE_COLORS = {
  admin: 'hsl(0,84%,60%)',
  executive: 'hsl(14,90%,60%)',
  factory_manager: 'hsl(197,100%,48%)',
  quality_manager: 'hsl(142,70%,45%)',
  warehouse_manager: 'hsl(180,70%,45%)',
  sales_manager: 'hsl(43,96%,56%)',
  dispatch_user: 'hsl(280,60%,60%)',
  procurement_user: 'hsl(320,60%,55%)',
  finance_user: 'hsl(60,80%,50%)',
  viewer: 'hsl(0,0%,55%)',
};

// Roles that can be scoped to specific locations
const SCOPED_ROLES = ['factory_manager', 'quality_manager', 'warehouse_manager', 'dispatch_user', 'procurement_user'];

const INP = {
  width: '100%', background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
  padding: '8px 10px', color: 'hsl(0,0%,85%)', fontFamily: "'JetBrains Mono', monospace",
  fontSize: 11, outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
  color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4,
};

export default function Users() {
  const { user: currentUser } = useAuth();
  const { locations } = useLocationContext();
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'factory_manager', assigned_locations: [], primary_location: '' });
  const [assignModal, setAssignModal] = useState(null); // { user }
  const [assignForm, setAssignForm] = useState({ assigned_locations: [], primary_location: '' });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = () => api.get('/users').then(r => { setUsers(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { if (currentUser?.role === 'admin') load(); }, [currentUser]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users', form);
      await load();
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'factory_manager', assigned_locations: [], primary_location: '' });
      showToast('User created successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create user', 'error');
    }
  };

  const openAssign = (u) => {
    setAssignForm({
      assigned_locations: u.assigned_locations || [],
      primary_location: u.primary_location || '',
    });
    setAssignModal({ user: u });
  };

  const toggleAssignLoc = (code) => {
    setAssignForm(prev => ({
      ...prev,
      assigned_locations: prev.assigned_locations.includes(code)
        ? prev.assigned_locations.filter(c => c !== code)
        : [...prev.assigned_locations, code],
      primary_location: prev.primary_location === code && prev.assigned_locations.includes(code)
        ? '' : prev.primary_location,
    }));
  };

  const handleSaveAssign = async () => {
    try {
      await api.put(`/users/${assignModal.user._id || assignModal.user.id}/locations`, assignForm);
      await load();
      setAssignModal(null);
      showToast('Location assignment saved');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save assignment', 'error');
    }
  };

  if (currentUser?.role !== 'admin') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <Warning size={48} color="hsl(0,84%,60%)" />
        <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, color: 'hsl(0,0%,60%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Access Restricted</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(0,0%,40%)' }}>This page is only accessible to System Admins.</div>
      </div>
    );
  }

  const roleCount = ROLES.reduce((acc, r) => { acc[r.value] = users.filter(u => u.role === r.value).length; return acc; }, {});

  // Show only main roles in stats (top 5)
  const STAT_ROLES = ROLES.slice(0, 5);

  return (
    <div data-testid="users-page">
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 20px',
          background: toast.type === 'error' ? 'hsl(0,80%,45%)' : 'hsl(142,70%,40%)',
          color: '#fff', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, letterSpacing: '0.05em',
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700, color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>User Management</h1>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {users.length} Total Users · Admin Only
          </div>
        </div>
        <button data-testid="add-user-btn" onClick={() => setShowAdd(true)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} weight="bold" /> ADD USER
        </button>
      </div>

      {/* Role Stats */}
      <div className="metrics-row" style={{ marginBottom: 24 }}>
        {STAT_ROLES.map(r => (
          <div key={r.value} style={{ flex: 1, background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{r.label}</div>
              <ShieldCheck size={14} color={ROLE_COLORS[r.value]} />
            </div>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 26, fontWeight: 700, color: ROLE_COLORS[r.value] }}>{roleCount[r.value] || 0}</div>
          </div>
        ))}
      </div>

      {/* Users Table */}
      <div style={{ background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr', padding: '10px 16px', borderBottom: '1px solid hsl(0,0%,12%)', gap: 12 }}>
          {['Name', 'Email', 'Role', 'Locations', 'Actions'].map(h => (
            <div key={h} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,40%)' }}>Loading...</div>
        ) : users.map(u => {
          const initials = (u.name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
          const isScoped = SCOPED_ROLES.includes(u.role);
          return (
            <div key={u._id || u.id} data-testid={`user-row-${u.email}`}
              style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr 1fr', padding: '12px 16px', borderBottom: '1px solid hsl(0,0%,8%)', gap: 12, alignItems: 'center', transition: 'background 0.1s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'hsl(0,0%,6%)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, background: u.avatar_url ? 'transparent' : ROLE_COLORS[u.role] || 'hsl(197,100%,48%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
                  {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
                </div>
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'hsl(0,0%,88%)', fontWeight: 600 }}>
                    {u.name}
                    {u.email === currentUser?.email && <span style={{ fontSize: 8, background: 'hsl(197,40%,15%)', color: 'hsl(197,100%,48%)', padding: '1px 6px', marginLeft: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>YOU</span>}
                  </div>
                  {u.phone && <div style={{ fontSize: 9, color: 'hsl(0,0%,40%)', fontFamily: "'JetBrains Mono', monospace" }}>{u.phone}</div>}
                </div>
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'hsl(0,0%,60%)' }}>{u.email}</div>
              <div>
                <span style={{ fontSize: 9, padding: '3px 8px', background: `${ROLE_COLORS[u.role]}20`, color: ROLE_COLORS[u.role], fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.08em', border: `1px solid ${ROLE_COLORS[u.role]}40` }}>
                  {ROLES.find(r => r.value === u.role)?.label || u.role}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {(u.assigned_locations || []).length === 0 ? (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,32%)', fontStyle: 'italic' }}>
                    {isScoped ? 'Unassigned' : 'All'}
                  </span>
                ) : (u.assigned_locations || []).map(code => (
                  <span key={code} style={{ fontSize: 8, padding: '2px 6px', background: code === u.primary_location ? 'hsl(197,40%,12%)' : 'hsl(0,0%,10%)', color: code === u.primary_location ? 'hsl(197,100%,48%)' : 'hsl(0,0%,55%)', fontFamily: "'JetBrains Mono', monospace", border: `1px solid ${code === u.primary_location ? 'hsl(197,60%,25%)' : 'hsl(0,0%,18%)'}`, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {code}
                  </span>
                ))}
              </div>
              <div>
                {isScoped && (
                  <button onClick={() => openAssign(u)} style={{ background: 'none', border: '1px solid hsl(0,0%,18%)', cursor: 'pointer', color: 'hsl(0,0%,50%)', padding: '4px 8px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'hsl(197,100%,48%)'; e.currentTarget.style.color = 'hsl(197,100%,48%)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'hsl(0,0%,18%)'; e.currentTarget.style.color = 'hsl(0,0%,50%)'; }}>
                    <MapPin size={10} /> Assign
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add User Modal */}
      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)', width: 480, padding: 28, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(0,0%,95%)', margin: 0 }}>Add New User</h3>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>
            <form onSubmit={handleAdd}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Full Name *</label>
                  <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Ravi Kumar" style={INP} data-testid="user-name-input" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Email Address *</label>
                  <input required type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="e.g. ravi@company.com" style={INP} data-testid="user-email-input" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Password *</label>
                  <input required type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Min. 8 characters" style={INP} data-testid="user-password-input" />
                </div>
                <div style={{ gridColumn: '1/-1' }}>
                  <label style={LBL}>Role *</label>
                  <select required value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value, assigned_locations: [], primary_location: '' }))} style={{ ...INP }} data-testid="user-role-select">
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {/* Location assignment — only for scoped roles */}
                {SCOPED_ROLES.includes(form.role) && locations.length > 0 && (
                  <>
                    <div style={{ gridColumn: '1/-1' }}>
                      <label style={LBL}>Assigned Locations</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {locations.map(loc => {
                          const active = form.assigned_locations.includes(loc.code);
                          return (
                            <button key={loc.code} type="button"
                              onClick={() => setForm(p => ({
                                ...p,
                                assigned_locations: active ? p.assigned_locations.filter(c => c !== loc.code) : [...p.assigned_locations, loc.code],
                                primary_location: p.primary_location === loc.code && active ? '' : p.primary_location,
                              }))}
                              style={{ padding: '4px 10px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid', transition: 'all 0.15s',
                                background: active ? 'hsl(197,40%,12%)' : 'transparent',
                                borderColor: active ? 'hsl(197,100%,48%)' : 'hsl(0,0%,20%)',
                                color: active ? 'hsl(197,100%,48%)' : 'hsl(0,0%,45%)',
                              }}>
                              {loc.code} · {loc.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {form.assigned_locations.length > 0 && (
                      <div style={{ gridColumn: '1/-1' }}>
                        <label style={LBL}>Primary Location</label>
                        <select style={{ ...INP }} value={form.primary_location} onChange={e => setForm(p => ({ ...p, primary_location: e.target.value }))}>
                          <option value="">— Select —</option>
                          {form.assigned_locations.map(code => {
                            const loc = locations.find(l => l.code === code);
                            return <option key={code} value={code}>{code} · {loc?.name || code}</option>;
                          })}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAdd(false)} className="btn-ghost">Cancel</button>
                <button type="submit" data-testid="save-user-btn" className="btn-primary">CREATE USER</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Location Assignment Modal */}
      {assignModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,18%)', width: 440, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <h3 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, textTransform: 'uppercase', color: 'hsl(0,0%,95%)', margin: 0 }}>Assign Locations</h3>
              <button onClick={() => setAssignModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)' }}><X size={18} /></button>
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'hsl(0,0%,45%)', marginBottom: 20 }}>
              {assignModal.user.name} · {ROLES.find(r => r.value === assignModal.user.role)?.label}
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={LBL}>Assigned Locations</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {locations.map(loc => {
                  const active = assignForm.assigned_locations.includes(loc.code);
                  return (
                    <button key={loc.code} type="button" onClick={() => toggleAssignLoc(loc.code)}
                      style={{ padding: '5px 12px', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', cursor: 'pointer', border: '1px solid', transition: 'all 0.15s',
                        background: active ? 'hsl(197,40%,12%)' : 'transparent',
                        borderColor: active ? 'hsl(197,100%,48%)' : 'hsl(0,0%,20%)',
                        color: active ? 'hsl(197,100%,48%)' : 'hsl(0,0%,45%)',
                      }}>
                      <MapPin size={9} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {loc.code} · {loc.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {assignForm.assigned_locations.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={LBL}>Primary Location</label>
                <select style={{ ...INP }} value={assignForm.primary_location} onChange={e => setAssignForm(p => ({ ...p, primary_location: e.target.value }))}>
                  <option value="">— None —</option>
                  {assignForm.assigned_locations.map(code => {
                    const loc = locations.find(l => l.code === code);
                    return <option key={code} value={code}>{code} · {loc?.name || code}</option>;
                  })}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setAssignModal(null)} className="btn-ghost">Cancel</button>
              <button onClick={handleSaveAssign} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={13} weight="bold" /> SAVE ASSIGNMENT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
